import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import React, { useCallback, useEffect, useRef, useState } from "react";
import CriticalNoticeCallRow from "./CriticalNoticeCallRow";
import PatientSearchInput from "Pages/Patient/PatientDemographic/PatientSearchInput";
import {
  getAll,
  getTabsConfiguration,
} from "Services/BloodLisCriticalNoticeCall";
import { Loader } from "Shared/Common/Loader";
import NoRecord from "Shared/Common/NoRecord";
import useLang from "Shared/hooks/useLanguage";
import usePagination from "Shared/hooks/usePagination";
import { ArrowDown, ArrowUp } from "Shared/Icons";
import CustomPagination from "Shared/JsxPagination";
import BreadCrumbs from "Utils/Common/Breadcrumb";
import { AutocompleteStyle } from "Utils/MuiStyles/AutocompleteStyles";

const TabSelected = styled(Tab)(AutocompleteStyle());

function a11yProps(index: number, tabID: number) {
  return {
    id: `critical-notice-tab-${tabID}`,
    "aria-controls": `critical-notice-tabpanel-${index}`,
  };
}

export interface FilterDataI {
  tabId: number | null;
  pageSize: number;
  pageNumber: number;
  sortColumn: string;
  sortDirection: string;
  filters: any[];
}

const initialFilterData: FilterDataI = {
  tabId: null,
  pageSize: 50,
  pageNumber: 1,
  sortColumn: "",
  sortDirection: "",
  filters: [],
};

const RECORDS_OPTIONS = [
  { value: 5, label: "5" },
  { value: 10, label: "10" },
  { value: 50, label: "50" },
  { value: 100, label: "100" },
];

const CriticalNoticeCall: React.FC = () => {
  const { t } = useLang();
  const [data, setData] = useState<{ gridHeaders: any[]; gridData: any[] }>({
    gridHeaders: [],
    gridData: [],
  });
  const [filterData, setFilterData] = useState<FilterDataI>(initialFilterData);
  const [searchValue, setSearchValue] = useState<Record<string, any>>({});
  const [searchedTags, setSearchedTags] = useState<string[]>([]);
  const [value, setValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [tabsConfigLoading, setTabsConfigLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | number | null>(null);
  const isFirstLoad = useRef(true);
  const filterDataRef = useRef(filterData);
  filterDataRef.current = filterData;

  const {
    curPage,
    pageSize,
    setPageSize,
    setCurPage,
    setTotal,
    total,
    totalPages,
    pageNumbers,
    nextPage,
    prevPage,
    showPage,
  } = usePagination();

  const loadTabs = useCallback(async () => {
    try {
      setTabsConfigLoading(true);
      const res: any = await getTabsConfiguration();
      const tabs = (res?.data?.data || []).sort(
        (a: any, b: any) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
      );
      setData((prev) => ({ ...prev, gridHeaders: tabs }));
      if (tabs.length > 0) {
        const firstTabId = tabs[0].tabID ?? null;
        setFilterData((prev) => ({ ...prev, tabId: firstTabId }));
        if (isFirstLoad.current) {
          setValue(0);
          isFirstLoad.current = false;
        }
      }
      return tabs;
    } catch {
      return [];
    } finally {
      setTabsConfigLoading(false);
    }
  }, []);

  const loadGridData = useCallback(
    async (showLoader = true, payload?: FilterDataI) => {
      const params = payload ?? {
        ...filterDataRef.current,
        pageNumber: curPage,
        pageSize,
      };
      if (params.tabId === undefined || params.tabId === null) return;
      if (showLoader) setLoading(true);
      try {
        const res: any = await getAll({
          tabId: params.tabId,
          pageNumber: params.pageNumber,
          pageSize: params.pageSize,
          sortColumn: params.sortColumn || undefined,
          sortDirection: params.sortDirection || undefined,
          filters: params.filters || [],
        });
        setData((prev) => ({
          ...prev,
          gridData: Array.isArray(res?.data?.data) ? res.data.data : [],
        }));
        setTotal(res?.data?.total ?? 0);
        if (payload) setFilterData(payload);
      } catch {
        setData((prev) => ({ ...prev, gridData: [] }));
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [curPage, pageSize]
  );

  useEffect(() => {
    loadTabs();
  }, [loadTabs]);

  useEffect(() => {
    if (filterData.tabId !== undefined && filterData.tabId !== null) {
      loadGridData(true, {
        ...filterDataRef.current,
        pageNumber: curPage,
        pageSize,
      });
    }
  }, [filterData.tabId, curPage, pageSize, loadGridData]);

  useEffect(() => {
    const activeTags = (filterData.filters || [])
      .filter(
        (f: any) =>
          f?.filterValue !== "" &&
          f?.filterValue !== undefined &&
          f?.filterValue !== null
      )
      .map((f: any) => String(f?.label ?? f?.columnName ?? "").trim())
      .filter((tag: string) => tag.length > 0);
    setSearchedTags(Array.from(new Set(activeTags)));
  }, [filterData.filters]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    const tabs = data.gridHeaders;
    const selectedTab = tabs?.[newValue];
    if (!selectedTab) return;
    setValue(newValue);
    setExpandedId(null);
    setSearchValue({});
    setFilterData((prev) => ({
      ...prev,
      tabId: selectedTab.tabID,
      filters: [],
      pageNumber: 1,
    }));
    setCurPage(1);
  };

  const handleSearch = () => {
    setCurPage(1);
    loadGridData(true, {
      ...filterData,
      pageNumber: 1,
      pageSize,
      filters: filterData.filters,
    });
  };

  const resetSearch = () => {
    setSearchValue({});
    setSearchedTags([]);
    setFilterData((prev) => ({ ...prev, filters: [], pageNumber: 1 }));
    setCurPage(1);
    loadGridData(true, {
      ...filterData,
      filters: [],
      pageNumber: 1,
      pageSize,
    });
  };

  const handleTagRemoval = (clickedTag: string) => {
    const updatedFilters = (filterData.filters || []).filter(
      (f: any) => String(f?.label ?? f?.columnName ?? "").trim() !== clickedTag
    );
    setSearchValue({});
    setCurPage(1);
    setFilterData((prev) => ({
      ...prev,
      filters: updatedFilters,
      pageNumber: 1,
    }));
    loadGridData(true, {
      ...filterData,
      filters: updatedFilters,
      pageNumber: 1,
      pageSize,
    });
  };

  const sortData = (tabsDetail: any) => {
    const nextDirection =
      filterData.sortColumn === tabsDetail?.columnKey &&
      filterData.sortDirection === "asc"
        ? "desc"
        : "asc";
    const payload: FilterDataI = {
      ...filterData,
      sortColumn: tabsDetail?.columnKey ?? "",
      sortDirection: nextDirection,
      pageNumber: 1,
      pageSize,
    };
    setFilterData(payload);
    setCurPage(1);
    loadGridData(true, payload);
  };

  const tabsInfo = data?.gridHeaders?.[value]?.tabHeaders ?? [];

  const showTableHead =
    tabsConfigLoading || loading || data.gridData.length > 0;

  const handleOrderCompleted = useCallback(() => {
    setExpandedId(null);
    loadGridData(true, { ...filterData, pageNumber: curPage, pageSize });
  }, [filterData, pageSize, curPage, loadGridData]);

  return (
    <>
      <div className="app-toolbar py-2 py-lg-3">
        <div className="app-container container-fluid d-flex flex-wrap gap-4 justify-content-center justify-content-sm-between align-items-center">
          <BreadCrumbs />
        </div>
      </div>
      <div className="d-flex flex-column flex-column-fluid">
        <div className="app-content flex-column-fluid">
          <div className="app-container container-fluid">
            <Tabs
              value={value}
              onChange={handleTabChange}
              className="min-h-auto"
              TabIndicatorProps={{ style: { background: "transparent" } }}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                "& .MuiTabs-scrollButtons": {
                  width: 0,
                  transition: "width 0.7s ease",
                  "&:not(.Mui-disabled)": {
                    width: "48px",
                  },
                },
              }}
            >
              {Array.isArray(data.gridHeaders) &&
                data.gridHeaders.map((tab: any, idx: number) => (
                  <TabSelected
                    key={tab?.tabID ?? idx}
                    label={t(tab?.tabName ?? "")}
                    {...a11yProps(idx, tab?.tabID)}
                    className="fw-bold text-capitalize"
                  />
                ))}
            </Tabs>
            <div className="card rounded-top-0 shadow-none tab-content-card">
              <div className="card-body py-2">
                <div className="d-flex gap-4 flex-wrap mb-1">
                  {searchedTags.map((tag) => (
                    <div
                      className="d-flex align-items-center cursor-pointer gap-1 p-2 rounded bg-light"
                      onClick={() => handleTagRemoval(tag)}
                      key={tag}
                    >
                      <span className="fw-bold">{tag}</span>
                      <i className="bi bi-x"></i>
                    </div>
                  ))}
                </div>
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                  <div className="d-flex align-items-center gap-2">
                    <span className="fw-400">{t("Records")}</span>
                    <select
                      className="form-select form-select-sm w-100px"
                      value={pageSize}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setPageSize(v);
                        setCurPage(1);
                        setFilterData((prev) => ({
                          ...prev,
                          pageSize: v,
                          pageNumber: 1,
                        }));
                      }}
                    >
                      {RECORDS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <button
                      type="button"
                      className="btn btn-linkedin btn-sm fw-500"
                      onClick={handleSearch}
                      disabled={tabsConfigLoading || loading}
                    >
                      {t("Search")}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm btn-secondary--icon fw-500"
                      onClick={resetSearch}
                      disabled={tabsConfigLoading || loading}
                    >
                      {t("Reset")}
                    </button>
                  </div>
                </div>

                <div className="card">
                  <Box sx={{ height: "auto", width: "100%" }}>
                    <div className="table_bordered overflow-hidden">
                      <TableContainer
                        component={Paper}
                        className="shadow-none"
                        sx={{ maxHeight: "calc(100vh - 190px)" }}
                      >
                        <Table
                          stickyHeader
                          className="table table-cutome-expend table-bordered table-sticky-header table-head-2-bg table-bg table-head-custom table-vertical-center border-0 mb-0"
                          sx={{
                            "& .MuiTableCell-root": { minWidth: 140 },
                            "& .MuiTableCell-root.w-50px": { minWidth: 50 },
                          }}
                        >
                          {showTableHead &&
                            (tabsConfigLoading ? (
                              <TableBody>
                                <TableRow>
                                  <Loader />
                                </TableRow>
                              </TableBody>
                            ) : (
                              <TableHead>
                                <TableRow className="h-40px">
                                  <TableCell className="w-50px" />
                                  {tabsInfo.map(
                                    (column: any, index: number) =>
                                      column?.isShowOnUi !== false &&
                                      !column?.isExpandData &&
                                      column?.isShow !== false && (
                                        <TableCell
                                          key={
                                            column?.columnKey ??
                                            column?.columnName ??
                                            index
                                          }
                                        >
                                          {(column?.filterColumns ??
                                            column?.columnKey) && (
                                            <PatientSearchInput
                                              loadData={(_reset = true) => {
                                                setCurPage(1);
                                                loadGridData(true, {
                                                  ...filterData,
                                                  pageNumber: 1,
                                                  pageSize,
                                                  filters: filterData.filters,
                                                });
                                              }}
                                              column={{
                                                ...column,
                                                filterColumns:
                                                  column?.filterColumns ??
                                                  column?.columnKey,
                                                columnLabel:
                                                  column?.columnLabel ??
                                                  column?.displayName,
                                              }}
                                              searchValue={{
                                                ...searchValue,
                                                filters: filterData.filters,
                                              }}
                                              filters={filterData.filters}
                                              setFilters={(
                                                updatedFilters: any[]
                                              ) => {
                                                setFilterData((prev) => ({
                                                  ...prev,
                                                  filters: updatedFilters,
                                                  pageNumber: 1,
                                                }));
                                              }}
                                              setSearchValue={setSearchValue}
                                              setCurPage={setCurPage}
                                            />
                                          )}
                                        </TableCell>
                                      )
                                  )}
                                </TableRow>
                                <TableRow className="h-30px">
                                  <TableCell className="w-50px" />
                                  {tabsInfo.map(
                                    (tabsDetail: any) =>
                                      tabsDetail?.isShowOnUi !== false &&
                                      tabsDetail?.isShow !== false && (
                                        <TableCell key={tabsDetail.columnKey}>
                                          <div
                                            className="d-flex justify-content-between cursor-pointer align-items-center gap-2"
                                            onClick={() =>
                                              tabsDetail?.columnKey &&
                                              sortData(tabsDetail)
                                            }
                                          >
                                            <span>
                                              {t(tabsDetail?.columnLabel ?? "")}
                                            </span>
                                            <span className="d-flex ms-2">
                                              <ArrowUp
                                                CustomeClass={
                                                  filterData.sortColumn ===
                                                    tabsDetail?.columnKey &&
                                                  filterData.sortDirection ===
                                                    "asc"
                                                    ? "text-success fs-7"
                                                    : "text-gray-700 fs-7 p-0 m-0"
                                                }
                                              />
                                              <ArrowDown
                                                CustomeClass={
                                                  filterData.sortColumn ===
                                                    tabsDetail?.columnKey &&
                                                  filterData.sortDirection ===
                                                    "desc"
                                                    ? "text-success fs-7"
                                                    : "text-gray-700 fs-7 p-0 m-0"
                                                }
                                              />
                                            </span>
                                          </div>
                                        </TableCell>
                                      )
                                  )}
                                </TableRow>
                              </TableHead>
                            ))}
                          <TableBody>
                            {tabsConfigLoading ? null : loading ? (
                              <TableRow>
                                <TableCell
                                  colSpan={(tabsInfo?.length ?? 0) + 1}
                                >
                                  <Loader />
                                </TableCell>
                              </TableRow>
                            ) : data.gridData.length > 0 ? (
                              data.gridData.map((row: any, index: number) => {
                                const rowId = row.RequisitionOrderId ?? index;
                                return (
                                  <CriticalNoticeCallRow
                                    key={rowId}
                                    row={row}
                                    tabsInfo={tabsInfo}
                                    activeTabName={
                                      data?.gridHeaders?.[value]?.tabName ?? ""
                                    }
                                    isExpanded={expandedId === rowId}
                                    onToggleExpand={() =>
                                      setExpandedId((prev) =>
                                        prev === rowId ? null : rowId
                                      )
                                    }
                                    onOrderCompleted={handleOrderCompleted}
                                  />
                                );
                              })
                            ) : (
                              <NoRecord
                                colSpan={Math.max(
                                  (tabsInfo?.length ?? 0) + 1,
                                  1
                                )}
                                cellSx={{
                                  py: 5,
                                  px: 2,
                                  borderBottom: "none",
                                }}
                              />
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </div>
                  </Box>
                </div>
                <CustomPagination
                  curPage={curPage}
                  nextPage={nextPage}
                  pageNumbers={pageNumbers}
                  pageSize={pageSize}
                  prevPage={prevPage}
                  showPage={showPage}
                  total={total}
                  totalPages={totalPages}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CriticalNoticeCall;
